import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { MonitorSmartphone, LogOut, RefreshCw } from "lucide-react";

const fmt = (iso) => {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
};

const idleLabel = (m) => (m < 1 ? "adesso" : m < 60 ? `${m} min fa` : `${Math.floor(m / 60)} h fa`);

export function SessionsCard() {
  const [data, setData] = useState({ sessions: [], token_days: 3 });
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/security/sessions").then((r) => setData(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 10000);
    return () => clearInterval(id);
  }, [load]);

  const revoke = async (s) => {
    if (!window.confirm(`Disconnettere ${s.full_name} (${s.device})?`)) return;
    setBusy(s.id);
    try {
      await api.delete(`/admin/security/sessions/${s.id}`);
      toast.success(`${s.full_name} disconnesso`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(null);
    }
  };

  const online = data.sessions.filter((s) => s.online).length;

  return (
    <Card className="bg-white border-[#DCE7F5] overflow-hidden" data-testid="sessions-card">
      <div className="p-5 border-b border-[#DCE7F5] flex items-center justify-between gap-3 flex-wrap">
        <div>
          <span className="font-display font-semibold text-[#0F1E33] flex items-center gap-2">
            <MonitorSmartphone className="w-4 h-4 text-[#0B6BCB]" strokeWidth={1.5} /> Sessioni attive
          </span>
          <p className="text-xs text-[#5A6B85] mt-1">
            <span data-testid="sessions-total">{data.sessions.length} dispositivi connessi</span> · {online} online ora · login richiesto ogni {data.token_days} giorni
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} className="border-[#DCE7F5] bg-transparent hover:bg-[#EEF4FC] text-[#0F1E33]" data-testid="sessions-refresh-btn">
          <RefreshCw className="w-4 h-4 mr-1.5" strokeWidth={1.5} /> Aggiorna
        </Button>
      </div>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-[#DCE7F5] hover:bg-transparent">
              <TableHead className="text-[#5A6B85]">Utente</TableHead>
              <TableHead className="text-[#5A6B85]">Dispositivo</TableHead>
              <TableHead className="text-[#5A6B85]">IP</TableHead>
              <TableHead className="text-[#5A6B85]">Accesso</TableHead>
              <TableHead className="text-[#5A6B85]">Ultima attività</TableHead>
              <TableHead className="text-[#5A6B85] text-right">Azione</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.sessions.map((s) => (
              <TableRow key={s.id} className="border-[#DCE7F5]" data-testid={`session-row-${s.id}`}>
                <TableCell>
                  <div className="font-medium text-[#0F1E33] flex items-center gap-2">
                    {s.full_name}
                    {s.is_current && <Badge className="bg-[#EEF4FC] text-[#0B6BCB] border-[#DCE7F5] text-[10px]">Questa sessione</Badge>}
                  </div>
                  <div className="text-xs text-[#94A3B8]">{s.email} · <span className="capitalize">{s.role}</span></div>
                </TableCell>
                <TableCell className="text-[#5A6B85] text-sm">{s.device}</TableCell>
                <TableCell className="text-[#5A6B85] text-sm">{s.ip}</TableCell>
                <TableCell className="text-[#5A6B85] text-sm">{fmt(s.created_at)}</TableCell>
                <TableCell>
                  <span className="flex items-center gap-2 text-sm text-[#5A6B85]">
                    <span className={`w-2 h-2 rounded-full ${s.online ? "bg-emerald-500" : "bg-[#CBD5E1]"}`} />
                    {idleLabel(s.idle_minutes)}
                  </span>
                </TableCell>
                <TableCell className="text-right">
                  <Button size="sm" variant="ghost" disabled={s.is_current || busy === s.id}
                    className="h-8 text-red-600 hover:bg-red-50 disabled:opacity-40"
                    onClick={() => revoke(s)} data-testid={`revoke-session-btn-${s.id}`}>
                    <LogOut className="w-4 h-4 mr-1" strokeWidth={1.5} /> Disconnetti
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {data.sessions.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-center text-[#5A6B85] py-8">Nessuna sessione attiva</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </Card>
  );
}
